// main imports
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Tab, Tabs } from "react-bootstrap";

// backend
import { getBackend } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as Auth from "@/lib/auth";
import * as Util from "@/lib/util";

// import subcomponents
import PeopleCompetence from "./PeopleCompetence";
import PeopleCompetenceModal from "./PeopleCompetenceModal";
import PeopleScheduleCalendar from "./PeopleScheduleCalendar";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ReactTable, { ReactTableColumn } from "@/modules/shared/ui/ReactTable";

type Props = {
    selected: t.IPersonData;
    lines: t.ILineData[];
    plants: t.IPlantData[];
    standalone?: boolean;
};

type DetailsTab = "competences" | "schedule";

type State = {
    show_modal: boolean;
    competences: t.ILineCompetenceData[];
    person_line_scores_by_line: Map<string, t.IPersonLineScore>;
    shifts: t.IShiftsPeopleEx[];
    error: string;
    warning: string;
    active_tab: DetailsTab;
    schedule_year: number;
    schedule_month: number;
};

/////////////////////////////////////////
// MAIN COMPONENT

/**
 * Displaying people details and manage competences.
 */
class PeopleDetails extends Component<Props, State> {
    /** Incremented on each competence/score fetch so slower older responses are ignored. */
    private competences_load_id = 0;
    /** Incremented on each schedule fetch so slower older responses are ignored. */
    private schedule_load_id = 0;

    state: State = {
        show_modal: false,
        competences: [],
        person_line_scores_by_line: new Map(),
        shifts: [],
        error: "",
        warning: "",
        active_tab: "competences",
        schedule_year: new Date().getFullYear(),
        schedule_month: new Date().getMonth(),
    };

    /** Load competences on first load */
    async componentDidMount() {
        await this.loadCompetences();
        await this.loadSchedule();
    }

    /**
     * Component updates by clicking another person. Competences need to be refreshed
     * from API.
     */
    async componentDidUpdate(prevProps: Props) {
        // we need to trigger reload only if it has not been triggered before
        if (prevProps.selected.uuid !== this.props.selected.uuid) {
            const now = new Date();
            this.setState({
                schedule_year: now.getFullYear(),
                schedule_month: now.getMonth(),
                competences: [],
                person_line_scores_by_line: new Map(),
                shifts: [],
                error: "",
                warning: "",
            });
            await this.loadCompetences();
            await this.loadSchedule(now.getFullYear(), now.getMonth());
        }
    }

    isCompetencesLoadStale(load_id: number, person_uuid: string): boolean {
        return load_id !== this.competences_load_id || this.props.selected.uuid !== person_uuid;
    }

    async loadCompetences() {
        const load_id = ++this.competences_load_id;
        const person_uuid = this.props.selected.uuid;
        const [competence_result, scores_result] = await Promise.allSettled([
            getBackend().manufacturing.getLineCompetence({ person_uuid }),
            getBackend().manufacturing.getPersonLineScores({ person_uuids: [person_uuid] }),
        ]);
        if (this.isCompetencesLoadStale(load_id, person_uuid)) {
            return;
        }

        let competences: t.ILineCompetenceData[] = [];
        let person_line_scores_by_line = new Map<string, t.IPersonLineScore>();
        let error = "";
        let warning = "";

        if (competence_result.status === "fulfilled") {
            competences = competence_result.value.competences;
        } else {
            error = (competence_result.reason as Error).message;
        }

        if (scores_result.status === "fulfilled") {
            for (const score of scores_result.value.person_line_scores) {
                person_line_scores_by_line.set(score.line_uuid, score);
            }
        } else {
            warning =
                (scores_result.reason as Error).message ||
                translate("Manufacturing.People.scores_load_error", "Failed to load person line scores");
        }

        this.setState({
            competences,
            person_line_scores_by_line,
            error,
            warning,
        });
    }

    async loadSchedule(year?: number, month?: number) {
        const load_id = ++this.schedule_load_id;
        try {
            const person_uuid = this.props.selected.uuid;
            const schedule_year = year ?? this.state.schedule_year;
            const schedule_month = month ?? this.state.schedule_month;
            // Start at first morning shift (06:00). Midnight maps to the previous day's
            // night shift via shiftNumber, which would pull the prior month's last night shift.
            const month_start = new Date(
                schedule_year,
                schedule_month,
                1,
                Util.SHIFT_CONSTS.FIRST_SHIFT_STARTING_HOUR,
                0,
                0
            );
            const month_end = new Date(schedule_year, schedule_month + 1, 0, 23, 59, 59);
            const data = await getBackend().manufacturing.getPeopleShiftsSimple({
                from_shift: Util.shiftNumber(month_start).shift_tag,
                person_uuid,
                to_shift: Util.shiftNumber(month_end).shift_tag,
            });
            if (load_id !== this.schedule_load_id) {
                return;
            }
            this.setState({ shifts: data.shifts_people, error: "" });
        } catch (err) {
            if (load_id !== this.schedule_load_id) {
                return;
            }
            this.setState({ error: (err as Error).message });
        }
    }

    handleScheduleMonthChange = async (year: number, month: number) => {
        this.setState({ schedule_year: year, schedule_month: month });
        await this.loadSchedule(year, month);
    };

    /**
     * Method triggered when Add/Update/Edit button is clicked. It opens competences modal.
     */
    handleModalShowClick() {
        this.setState({ show_modal: true });
    }

    /**
     * Method triggered when modal close button (or modal background overlay) is clicked.
     * It closes the modal.
     */
    async handleModalCloseClick(do_refresh: boolean) {
        if (do_refresh) {
            await this.loadCompetences();
        }

        this.setState({ show_modal: false });
    }

    handleTabSelect = (tab_key: string | null) => {
        if (tab_key === "competences" || tab_key === "schedule") {
            this.setState({ active_tab: tab_key });
        }
    };

    renderTags() {
        const keys = Object.keys(this.props.selected.tags).sort((a, b) => a.localeCompare(b));
        const result: React.ReactNode[] = [];
        for (const key of keys) {
            result.push(
                <div key={key}>
                    <dt>{translate(`Manufacturing.People.${key}`, key)}</dt>
                    <dd>{this.props.selected.tags[key]}</dd>
                </div>
            );
        }
        return result;
    }

    renderCompetences() {
        const { competences, person_line_scores_by_line } = this.state;
        if (competences.length === 0) {
            return null;
        }

        competences.sort((a, b) => {
            if (a.plant_title !== b.plant_title) {
                return Util.stringCompare(a.plant_title, b.plant_title);
            }

            return Util.stringCompare(a.line_title, b.line_title);
        });

        let score_max = 0;
        for (const competence of competences) {
            const line_score = person_line_scores_by_line.get(competence.line_uuid);
            if (line_score && typeof line_score.score === "number" && !Number.isNaN(line_score.score)) {
                score_max = Math.max(score_max, line_score.score);
            }
        }

        const plant_uuids = [...new Set(competences.map(x => x.plant_uuid))];

        return plant_uuids.map(plant_uuid => {
            const competences_for_plant = competences.filter(x => x.plant_uuid === plant_uuid);
            return (
                <React.Fragment key={plant_uuid}>
                    <h5>{competences_for_plant[0].plant_title}</h5>
                    <ul className="list-group list-group-flush">
                        {competences_for_plant.map((competence, i) => {
                            const line_score = person_line_scores_by_line.get(competence.line_uuid);
                            return (
                                <PeopleCompetence
                                    key={`competence_${plant_uuid}_${i}`}
                                    level={competence.level}
                                    title={competence.line_title}
                                    line_uuid={competence.line_uuid}
                                    score={line_score?.score}
                                    score_max={score_max}
                                    warmup={line_score?.warmup}
                                    dwell={line_score?.dwell}
                                />
                            );
                        })}
                    </ul>
                </React.Fragment>
            );
        });
    }

    renderSchedule() {
        const columns: ReactTableColumn<t.IShiftsPeopleEx>[] = [
            {
                Header: translate("common.date", "Date"),
                accessor: "shift_tag",
                disableFilters: true,
                Cell: val => {
                    const rec: t.IShiftsPeopleEx = val.cell.row.original;
                    const d = Util.fromShiftTag(rec.shift_tag);
                    return Util.niceDateTime(d);
                },
            },
            {
                Header: translate("common.shift", "Shift"),
                accessor: "shift_number",
                disableFilters: true,
                Cell: val => {
                    const rec: t.IShiftsPeopleEx = val.cell.row.original;
                    const day_shift = Util.weekToDayShift(rec.shift_number) + 1;
                    return <span className={"badge badge-shift badge-shift-" + day_shift}>{day_shift}</span>;
                },
            },
            {
                Header: translate("common.line", "Line"),
                accessor: "line_title",
                disableFilters: true,
            },
        ];
        const data = this.state.shifts.slice().sort((a, b) => a.shift_tag.localeCompare(b.shift_tag));
        return (
            <div className="mt-3">
                <PeopleScheduleCalendar
                    shifts={this.state.shifts}
                    year={this.state.schedule_year}
                    month={this.state.schedule_month}
                    onMonthChange={this.handleScheduleMonthChange}
                />
                {data.length === 0 ? (
                    <FormattedMessage id="Manufacturing.People.no_schedule" defaultMessage="No schedule available" />
                ) : (
                    <ReactTable data={data} columns={columns} />
                )}
            </div>
        );
    }

    renderCompetencesTab(can_edit: boolean) {
        const groupped_competences = this.renderCompetences();
        return (
            <div className="mt-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                    {can_edit && (
                        <button
                            className="btn btn-outline-primary ml-auto my-1"
                            onClick={() => this.handleModalShowClick()}
                        >
                            <FormattedMessage
                                id="Manufacturing.People.edit_competences"
                                defaultMessage="Edit competences"
                            />
                        </button>
                    )}
                    {this.state.show_modal && (
                        <PeopleCompetenceModal
                            show={this.state.show_modal}
                            lines={this.props.lines}
                            person_uuid={this.props.selected.uuid}
                            person_name={this.props.selected.name}
                            plant_uuids={this.props.selected.plant_uuids}
                            handleModalCloseClick={(do_refresh: boolean) => {
                                this.handleModalCloseClick(do_refresh);
                            }}
                        />
                    )}
                </div>
                <div className="competences">
                    <div className="card">
                        {groupped_competences !== null && groupped_competences}
                        {groupped_competences === null && (
                            <h5>
                                <FormattedMessage
                                    id="Manufacturing.People.no_competences"
                                    defaultMessage="No competences"
                                />
                            </h5>
                        )}
                        <div className="space15"></div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (this.props.selected.uuid) {
            const { name, uuid, external_id, ted_id, adjuster, shift_preference, plant_uuids } = this.props.selected;
            const can_edit = Auth.hasPermission(Auth.PERMISSION_NAMES.PeopleEdit);

            const plants_hash = Object.fromEntries(this.props.plants.map(plant => [plant.uuid, plant.title]));
            const plants = plant_uuids.map(uuid => plants_hash[uuid]).join(", ");

            const shiftBadges = shift_preference.map((val, i) => {
                const shift = i + 1;
                return val ? (
                    <span key={i} className={"badge badge-shift badge-shift-" + shift}>
                        {shift}
                    </span>
                ) : null;
            });

            // if we are in standalone mode back link on edit page is different
            const edit_url = this.props.standalone
                ? `/digital-twin/resources/person/edit/${uuid}?back=view`
                : `/digital-twin/resources/person/edit/${uuid}`;

            return (
                <div>
                    <ErrorComponent msg={this.state.error} type="error" />
                    <ErrorComponent msg={this.state.warning} type="warning" />
                    <div className="tab-content">
                        <div className="tab-pane active tab-pane-text" id="home">
                            <div className="d-flex">
                                {can_edit && (
                                    <div className="button-list order-last ml-auto">
                                        <Link className="btn btn-outline-primary" to={edit_url}>
                                            <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                        </Link>
                                    </div>
                                )}
                                <div className="order-first">
                                    <h5 className="pane-title">
                                        {name}{" "}
                                        {adjuster && (
                                            <span className="badge-list">
                                                <span className="badge badge-primary">
                                                    <FormattedMessage id="common.adjuster" defaultMessage="adjuster" />
                                                </span>
                                            </span>
                                        )}
                                    </h5>
                                    <dl className="property-list">
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="common.personel_id"
                                                    defaultMessage="Personel ID"
                                                />
                                            </dt>
                                            <dd>
                                                <div>{external_id}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage id="common.linegroup" defaultMessage="Line-group" />
                                            </dt>
                                            <dd>
                                                <div>{ted_id}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="Manufacturing.People.allowed_shifts"
                                                    defaultMessage="Allowed shifts"
                                                />
                                            </dt>
                                            <dd>
                                                <span className="badge-list">{shiftBadges}</span>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage
                                                    id="Manufacturing.People.plant_membership"
                                                    defaultMessage="Plant membership"
                                                />
                                            </dt>
                                            <dd>
                                                <div>{plants}</div>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                <FormattedMessage id="common.tags" defaultMessage="Tags" />
                                            </dt>
                                            <div>{this.renderTags()}</div>
                                        </div>
                                        <div>
                                            <dt className="light">
                                                <FormattedMessage id="common.uuid" defaultMessage="UUID" />
                                            </dt>
                                            <dd className="light">
                                                <div>{uuid}</div>
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                            <Tabs
                                id="people-details-tabs"
                                activeKey={this.state.active_tab}
                                onSelect={this.handleTabSelect}
                                className="mt-3"
                            >
                                <Tab
                                    eventKey="competences"
                                    title={translate("Manufacturing.People.competences", "Competences")}
                                >
                                    {this.renderCompetencesTab(can_edit)}
                                </Tab>
                                <Tab eventKey="schedule" title={translate("common.schedule", "Schedule")}>
                                    {this.renderSchedule()}
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                </div>
            );
        }

        // return empty element if there are no available competences
        return <div />;
    }
}

export default PeopleDetails;
